import { useEffect, useMemo, useRef, useState } from 'react';

import {
  formatOptionSymbol,
  type OptionIdentifier,
  type OptionPricePoint,
} from '@/types/optionDiscovery';
import { useAccount } from '@hooks/useAccount';
import { usePrices } from '@hooks/usePrices';
import {
  calculateOptionPriceAtPoint,
  calculateOptionPriceHistory,
  fetchExtendedPriceHistory,
} from '@services/optionDiscoveryService';
import { OptionCalculator } from '@services/optionService';
import type { SecondPricePoint } from '@services/priceService';
import { getCurrentTime } from '@services/timeshiftService';

export interface UseOptionChartResult {
  symbol: string;
  priceHistory: OptionPricePoint[];
  currentPrice: number | null;
  spotPrice: number | null;
  bidPrice: number | null;
  askPrice: number | null;
  highPrice: number | null;
  lowPrice: number | null;
  isLoading: boolean;
  isReady: boolean;
  userOwnsOption: boolean;
  userQuantity: number;
}

const REFRESH_INTERVAL_MS = 10000; // 10 seconds for chart history recalculation
const REFETCH_INTERVAL_MS = 60 * 1000; // Refetch candles every 1 minute to get new data points

export function useOptionChart(
  option: OptionIdentifier | null,
): UseOptionChartResult {
  const [priceHistory, setPriceHistory] = useState<OptionPricePoint[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { positions } = useAccount();

  // Get live price data for real-time updates
  const pricesQuery = usePrices();
  const livePrices = pricesQuery.data;

  // Calculate symbol
  const symbol = useMemo(() => {
    if (!option) {
      return '';
    }
    return formatOptionSymbol(option.asset, option.strikePrice, option.type);
  }, [option]);

  // Check if user owns this option
  const { userOwnsOption, userQuantity } = useMemo(() => {
    if (!option) {
      return { userOwnsOption: false, userQuantity: 0 };
    }

    const matchingPositions = positions.filter(
      pos =>
        pos.asset === option.asset &&
        pos.strikePrice === option.strikePrice &&
        pos.type === option.type &&
        pos.expiryTimestamp === option.expiryTimestamp,
    );

    const totalQuantity = matchingPositions.reduce(
      (sum, pos) => sum + pos.quantity,
      0,
    );

    return {
      userOwnsOption: totalQuantity > 0,
      userQuantity: totalQuantity,
    };
  }, [option, positions]);

  // Calculate live point and append to history for real-time chart updates
  const {
    liveHistory,
    currentPrice,
    spotPrice,
    bidPrice,
    askPrice,
    highPrice,
    lowPrice,
  } = useMemo(() => {
    if (!option || priceHistory.length === 0) {
      return {
        liveHistory: [],
        currentPrice: null,
        spotPrice: null,
        bidPrice: null,
        askPrice: null,
        highPrice: null,
        lowPrice: null,
      };
    }

    const lastPoint = priceHistory[priceHistory.length - 1];

    // Get LIVE spot price from WebSocket data (reactive to price changes)
    const liveSpotPrice = livePrices?.get(option.asset)?.price;
    const currentSpotPrice = liveSpotPrice ?? lastPoint.spotPrice;

    // Calculate current option price using LIVE spot price and volatility
    // This gives real-time updates as spot price moves
    const currentVolatility = lastPoint.volatility;
    const now = getCurrentTime();
    const liveOptionPrice = calculateOptionPriceAtPoint(
      option,
      currentSpotPrice,
      currentVolatility,
      now,
    );

    // Use live calculated price, falling back to last historical point
    const midPrice = liveOptionPrice > 0 ? liveOptionPrice : lastPoint.price;

    // Create a live point and append to history for chart rendering
    const livePoint: OptionPricePoint = {
      timestamp: now,
      price: midPrice,
      spotPrice: currentSpotPrice,
      volatility: currentVolatility,
    };

    // Append live point to history (creates new array for React to detect change)
    const historyWithLive = [...priceHistory, livePoint];

    // Calculate high/low including the live point
    const prices = historyWithLive.map(p => p.price);
    const high = Math.max(...prices);
    const low = Math.min(...prices);

    // Calculate bid/ask spread from the mid price
    const orderBook = OptionCalculator.calculateOrderBookPricing(
      midPrice,
      currentSpotPrice,
      option.strikePrice,
      option.expiryTimestamp,
    );

    return {
      liveHistory: historyWithLive,
      currentPrice: midPrice,
      spotPrice: currentSpotPrice,
      bidPrice: orderBook.bid,
      askPrice: orderBook.ask,
      highPrice: high,
      lowPrice: low,
    };
  }, [option, priceHistory, livePrices]);

  // Store the base spot price history (fetched once, then periodically refreshed)
  const spotHistoryRef = useRef<SecondPricePoint[]>([]);
  const lastFetchedOptionKey = useRef<string>('');
  const lastFetchTime = useRef<number>(0);

  // Fetch extended history and calculate option prices
  useEffect(() => {
    if (!option) {
      setPriceHistory([]);
      setIsLoading(false);
      spotHistoryRef.current = [];
      return;
    }

    // Wait for price service to bootstrap
    if (pricesQuery.isLoading) {
      return;
    }

    let cancelled = false;
    const optionKey = `${option.asset}-${option.strikePrice}-${option.type}`;

    // Fetch extended history (5-minute candles with 24h+ lookback)
    const fetchHistory = async (showLoading = true) => {
      if (showLoading) {
        setIsLoading(true);
      }

      try {
        // Fetch enough data for option lifetime + 24h volatility lookback
        const extendedHistory = await fetchExtendedPriceHistory(option.asset);

        if (cancelled) {
          return;
        }

        if (extendedHistory.length < 200) {
          console.warn(
            `Not enough candle data for ${option.asset}: ${extendedHistory.length} candles`,
          );
          setPriceHistory([]);
          setIsLoading(false);
          return;
        }

        // Store as our base history
        spotHistoryRef.current = extendedHistory;
        lastFetchedOptionKey.current = optionKey;
        lastFetchTime.current = getCurrentTime();

        // Calculate option price history
        const history = calculateOptionPriceHistory(option, extendedHistory);
        
        // If history is empty, it might be because the option just started
        // Keep loading state true so we retry on next interval
        if (history.length === 0) {
          console.warn(
            `No option price history calculated for ${option.asset} ${option.type} ${option.strikePrice}. Option may have just started.`,
          );
          // Don't set isLoading to false - let the interval retry
          return;
        }
        
        setPriceHistory(history);
        setIsLoading(false);
      } catch (err) {
        console.error('Error fetching option price history:', err);
        if (!cancelled) {
          setPriceHistory([]);
          setIsLoading(false);
        }
      }
    };

    // Recalculate from existing data (no fetch)
    const recalculate = () => {
      if (cancelled || spotHistoryRef.current.length === 0) {
        return;
      }

      const history = calculateOptionPriceHistory(
        option,
        spotHistoryRef.current,
      );
      
      // If recalculation results in empty history, we need to refetch
      if (history.length === 0) {
        fetchHistory();
        return;
      }
      
      setPriceHistory(history);
    };

    // Initial fetch or use cached data
    if (
      lastFetchedOptionKey.current === optionKey &&
      spotHistoryRef.current.length > 0
    ) {
      // Same option - just recalculate from cached data
      recalculate();
    } else {
      // Different option - fetch fresh data
      fetchHistory();
    }

    // Set up interval for periodic refresh
    const intervalId = setInterval(() => {
      if (cancelled) {
        return;
      }

      const now = getCurrentTime();
      if (now - lastFetchTime.current > REFETCH_INTERVAL_MS) {
        // Time to fetch new candles
        fetchHistory(false);
      } else {
        // Just recalculate (in case time-to-expiry changed significantly)
        // If recalculation fails, it will trigger a refetch
        recalculate();
      }
    }, REFRESH_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(intervalId);
    };
  }, [option, pricesQuery.isLoading]);

  const isReady = !isLoading && liveHistory.length > 0;

  return useMemo(
    () => ({
      symbol,
      priceHistory: liveHistory, // Return history with live point appended
      currentPrice,
      spotPrice,
      bidPrice,
      askPrice,
      highPrice,
      lowPrice,
      isLoading,
      isReady,
      userOwnsOption,
      userQuantity,
    }),
    [
      symbol,
      liveHistory,
      currentPrice,
      spotPrice,
      bidPrice,
      askPrice,
      highPrice,
      lowPrice,
      isLoading,
      isReady,
      userOwnsOption,
      userQuantity,
    ],
  );
}
